import apiClient from "./index";
import API_BASE_URL from "./API_BASE_URL";

/** The version of each policy the account holder accepted (task 164). */
export interface AcceptedPolicies {
  terms: string;
  privacy: string;
}

export interface SignupPayload {
  fullName: string;
  email: string;
  password: string;
  phoneNumber: string;
  acceptTerms: boolean;
  /**
   * Required. The server refuses a signup that does not name the current
   * version of each document — a stale version is refused as firmly as a
   * missing one, so this is read from the API rather than hardcoded.
   */
  acceptedPolicies: AcceptedPolicies;
}

export interface LoginPayload {
  email: string;
  password: string;
}

export interface LoginResponse {
  data: {
    message: string;
    token: string;
    user: {
      id: number;
      email: string;
      fullName: string;
      phoneNumber: string;
      acceptTerms: boolean;
      googleId: string | null;
      isEmailVerified: boolean;
      emailVerifiedAt: string | null;
      createdAt: string;
      updatedAt: string;
    };
  };
}

export interface VerificationRequiredResponse {
  message: string;
  code: "VERIFICATION_REQUIRED" | "EMAIL_SEND_FAILED";
  verificationRequired: true;
  verificationToken: string;
  expiresAt: string;
  resendAvailableAt: string;
  serverTime: string;
  emailDeliveryError?: boolean;
  user: LoginResponse["data"]["user"];
}

export interface CheckUserByEmailResponse {
  data: {
    exists: boolean;
  };
}

export const signup = (payload: SignupPayload) =>
  apiClient.post("/auth/signup", payload);

/** The versions a signup must accept. Public; no account needed to read it. */
export const getPolicyVersions = () =>
  apiClient.get<{ currentVersions: AcceptedPolicies }>("/auth/policy-versions");

const GOOGLE_BASE = `${API_BASE_URL.replace(/\/$/, "")}/auth/google`;

/**
 * Plain sign-in URL. Signing in with an existing Google account needs no
 * acceptance, so this carries none.
 */
export const googleLoginUrl = GOOGLE_BASE;

/**
 * Sign-UP URL. A new Google account is a signup, so acceptance has to travel
 * with it: the server signs these versions into the OAuth state and refuses
 * to create an account without them.
 */
export const googleSignupUrl = (accepted: AcceptedPolicies) =>
  `${GOOGLE_BASE}?terms=${encodeURIComponent(accepted.terms)}`
  + `&privacy=${encodeURIComponent(accepted.privacy)}`;
export const googleFailureUrl = `${API_BASE_URL.replace(
  /\/$/,
  ""
)}/auth/google/failure`;
export const googleCallbackPath = "/auth/google/callback";

export const login = (payload: LoginPayload) => {
  return apiClient.post<LoginResponse["data"] | VerificationRequiredResponse>("/auth/login", payload);
};

export const verifyEmailOtp = (verificationToken: string, otp: string) =>
  apiClient.post<LoginResponse["data"]>(
    "/auth/verify-email",
    { otp },
    { headers: { Authorization: `Bearer ${verificationToken}` } },
  );

export const resendEmailVerification = (verificationToken: string) =>
  apiClient.post<Pick<VerificationRequiredResponse, "message" | "verificationRequired" | "expiresAt" | "resendAvailableAt" | "serverTime">>(
    "/auth/resend-verification",
    null,
    { headers: { Authorization: `Bearer ${verificationToken}` } },
  );

export const checkUserByEmail = (email: string) => {
  return apiClient.get<CheckUserByEmailResponse>(
    `/auth/email/${encodeURIComponent(email)}`
  );
};

// Password Reset Interfaces
export interface ForgotPasswordPayload {
  email: string;
}

export interface ResetPasswordPayload {
  password: string;
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

/**
 * Request password reset link
 * @param payload - Email address for password reset
 */
export const forgotPassword = (payload: ForgotPasswordPayload) => {
  return apiClient.post("/auth/forgot-password", payload);
};

/**
 * Reset password using token from email
 * @param token - Reset token from email
 * @param payload - New password
 */
export const resetPassword = (token: string, payload: ResetPasswordPayload) => {
  return apiClient.post(`/auth/reset-password/${token}`, payload);
};

/**
 * Change password (authenticated user, e.g. from Profile/Settings).
 * Backend: POST /auth/change-password with currentPassword, newPassword.
 */
export const changePassword = (payload: ChangePasswordPayload) => {
  return apiClient.post("/auth/change-password", payload);
};
